import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { get } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import Pagination from '../../../../components/pagination/Pagination'
import FormLoader from '../../../../components/loader/FormLoader'
import { IntelligentCheck, IntelligentCheckBadge } from './IntelligentCheck'
import './KycReview.scss'

type TabKey = 'pending_review' | 'approved' | 'rejected' | 'expired'

export interface KycUser {
  _id: string
  firstname: string
  lastname: string
  username?: string
  email: string
  phonenumber: string
  createdAt?: string
  kycStatus?: string
  kycDeadline?: string
}

export interface KycSubmission {
  _id: string
  version: number
  status: string
  ninMasked: string
  submittedVia: 'user' | 'agent'
  submittedBy?: { name: string; email: string; role: string } | null
  reviewedBy?: { name: string; email: string; role: string } | null
  reviewedAt?: string
  rejectionReason?: string
  createdAt: string
  userId: KycUser | null
  idDocument?: { documentType: string }
  proofOfAddress?: { documentType: string; documentDate: string }
  intelligentCheck?: IntelligentCheck
}

interface KycStats {
  pending_review: number
  approved: number
  rejected: number
  expired: number
  usersNotSubmitted: number
  pendingByIntelligentCheck?: IntelligentCheckCounts
}

type IntelligentCheckCounts = { pass: number; flag: number; fail: number; unreviewed: number }

// Intelligent check verdict filter → ?intelligentCheck= on the queue
const INTELLIGENT_CHECK_FILTERS: { key: string; label: string }[] = [
  { key: '', label: 'All' },
  { key: 'pass', label: 'Pass' },
  { key: 'flag', label: 'Flag' },
  { key: 'fail', label: 'Fail' },
  { key: 'unreviewed', label: 'Unreviewed' },
]

const TABS: { key: TabKey; label: string }[] = [
  { key: 'pending_review', label: 'Pending Review' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'expired', label: 'Expired' },
]

export const KYC_STATUS_META: {
  [key: string]: { label: string; className: string }
} = {
  pending_review: { label: 'Pending Review', className: 'pill-pending' },
  approved: { label: 'Approved', className: 'pill-approved' },
  rejected: { label: 'Rejected', className: 'pill-rejected' },
  expired: { label: 'Expired', className: 'pill-expired' },
  not_submitted: { label: 'Not Submitted', className: 'pill-none' },
}

export const KycStatusPill = ({ status }: { status?: string }) => {
  const meta = KYC_STATUS_META[status || 'not_submitted'] || KYC_STATUS_META.not_submitted
  return <span className={`kyc-pill ${meta.className}`}>{meta.label}</span>
}

export const waitingDays = (createdAt: string) => {
  const days = Math.floor(
    (Date.now() - new Date(createdAt).getTime()) / (24 * 60 * 60 * 1000),
  )
  if (days <= 0) return 'Today'
  return `${days} day${days === 1 ? '' : 's'}`
}

const KycReview = () => {
  const navigate = useNavigate()
  const { profile, notify } = useGlobalContext()

  const [activeTab, setActiveTab] = useState<TabKey>('pending_review')
  const [submissions, setSubmissions] = useState<KycSubmission[]>([])
  const [pagination, setPagination] = useState<any>(null)
  const [stats, setStats] = useState<KycStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [checkFilter, setCheckFilter] = useState('')

  const canReview =
    profile?.role === 'super admin' || profile?.role === 'customer service'

  const fetchQueue = (page = 1, limit = 20) => {
    setLoading(true)
    let url = `kyc/admin/queue?status=${activeTab}&page=${page}&limit=${limit}`
    if (search) url += `&search=${encodeURIComponent(search)}`
    if (checkFilter) url += `&intelligentCheck=${checkFilter}`
    get(url)
      .then((res: any) => {
        if (res.success) {
          setSubmissions(res.data.docs)
          setPagination(res.data)
        } else notify('error', res.message)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  const fetchStats = () => {
    get('kyc/admin/stats').then((res: any) => {
      if (res.success) setStats(res.data)
    })
  }

  useEffect(() => {
    if (!canReview) return
    fetchQueue()
    fetchStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, search, checkFilter])

  if (!canReview) {
    return (
      <div className='kyc-review'>
        <div className='restricted'>
          <Icon icon='mdi:shield-lock' />
          <h1>Restricted</h1>
          <p>KYC review is only available to super admins and customer service.</p>
        </div>
      </div>
    )
  }

  const tabCount = (key: TabKey) => (stats ? stats[key] : null)

  return (
    <div className='kyc-review'>
      <div className='hero'>
        <div className='hero-text'>
          <h1>KYC Verification</h1>
          <p>Review customer identity documents before they can keep booking.</p>
        </div>
        {stats && (
          <div className='hero-stats'>
            <div className='stat urgent'>
              <h2>{stats.pending_review}</h2>
              <p>Awaiting review</p>
            </div>
            <div className='stat'>
              <h2>{stats.approved}</h2>
              <p>Verified</p>
            </div>
            <div className='stat'>
              <h2>{stats.usersNotSubmitted}</h2>
              <p>Not yet submitted</p>
            </div>
          </div>
        )}
      </div>

      <div className='header-tabs'>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={activeTab === tab.key ? 'active' : ''}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            {tabCount(tab.key) !== null && (
              <span className='count'>{tabCount(tab.key)}</span>
            )}
          </button>
        ))}
      </div>

      <div className='search-row flex'>
        <div className='search flex'>
          <input
            type='text'
            placeholder='Search name, email or phone…'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput)}
          />
          <button onClick={() => setSearch(searchInput)}>
            <Icon icon='mdi:magnify' /> Search
          </button>
        </div>
        <div className='ic-filter flex'>
          <span className='ic-filter-label'>
            <Icon icon='mdi:shield-search' /> Intelligent check
          </span>
          {INTELLIGENT_CHECK_FILTERS.map((f) => {
            const count =
              activeTab === 'pending_review' && f.key && stats?.pendingByIntelligentCheck
                ? stats.pendingByIntelligentCheck[f.key as keyof IntelligentCheckCounts]
                : null
            return (
              <button
                key={f.key || 'all'}
                className={checkFilter === f.key ? 'active' : ''}
                onClick={() => setCheckFilter(f.key)}
              >
                {f.label}
                {count !== null && count !== undefined && (
                  <span className='count'>{count}</span>
                )}
              </button>
            )
          })}
        </div>
        {search && (
          <button
            className='clear-search'
            onClick={() => {
              setSearch('')
              setSearchInput('')
            }}
          >
            Clear <Icon icon='mdi:close' />
          </button>
        )}
      </div>

      <div className='queue-card'>
        {loading ? (
          <FormLoader position='center' />
        ) : submissions.length === 0 ? (
          <div className='empty-state'>
            <Icon icon='mdi:shield-check-outline' />
            <h3>
              {activeTab === 'pending_review'
                ? 'Queue is clear'
                : 'Nothing here yet'}
            </h3>
            <p>
              {activeTab === 'pending_review'
                ? 'No submissions are waiting for review.'
                : `No ${TABS.find((t) => t.key === activeTab)?.label.toLowerCase()} submissions found.`}
            </p>
          </div>
        ) : (
          <>
            <div className='table-wrap desktop-table'>
              <table>
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>NIN</th>
                    <th>Submitted</th>
                    <th>Waiting</th>
                    <th>Channel</th>
                    {activeTab !== 'pending_review' && <th>Reviewed By</th>}
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((sub) => (
                    <tr
                      key={sub._id}
                      onClick={() => navigate(`/admin/kyc-request/${sub._id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div className='customer-cell'>
                          <div className='avatar'>
                            {sub.userId?.firstname?.[0]}
                            {sub.userId?.lastname?.[0]}
                          </div>
                          <div>
                            <p className='name'>
                              {sub.userId?.firstname} {sub.userId?.lastname}
                            </p>
                            <p className='email'>{sub.userId?.email}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className='mono'>{sub.ninMasked}</span>
                      </td>
                      <td>{new Date(sub.createdAt).toLocaleDateString()}</td>
                      <td>
                        <span
                          className={`waiting ${
                            sub.status === 'pending_review' &&
                            Date.now() - new Date(sub.createdAt).getTime() >
                              3 * 24 * 60 * 60 * 1000
                              ? 'late'
                              : ''
                          }`}
                        >
                          {waitingDays(sub.createdAt)}
                        </span>
                      </td>
                      <td>
                        {sub.submittedVia === 'agent' ? (
                          <span
                            className='via-badge agent'
                            title={sub.submittedBy ? `By ${sub.submittedBy.name}` : ''}
                          >
                            <Icon icon='mdi:account-tie' /> Agent
                          </span>
                        ) : (
                          <span className='via-badge user'>
                            <Icon icon='mdi:account' /> Customer
                          </span>
                        )}
                      </td>
                      {activeTab !== 'pending_review' && (
                        <td>{sub.reviewedBy?.name || '—'}</td>
                      )}
                      <td>
                        <div className='status-cell'>
                          <KycStatusPill status={sub.status} />
                          <IntelligentCheckBadge review={sub.intelligentCheck} />
                        </div>
                      </td>
                      <td className='icons' onClick={(e) => e.stopPropagation()}>
                        <button
                          className='review-btn'
                          onClick={() => navigate(`/admin/kyc-request/${sub._id}`)}
                        >
                          {sub.status === 'pending_review' ? 'Review' : 'View'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className='mobile-cards'>
              {submissions.map((sub) => (
                <div
                  className='card'
                  key={sub._id}
                  onClick={() => navigate(`/admin/kyc-request/${sub._id}`)}
                >
                  <div className='row'>
                    <p className='name'>
                      {sub.userId?.firstname} {sub.userId?.lastname}
                    </p>
                    <KycStatusPill status={sub.status} />
                  </div>
                  <div className='row'>
                    <p>{sub.userId?.email}</p>
                  </div>
                  <div className='row'>
                    <p>
                      Submitted {new Date(sub.createdAt).toLocaleDateString()} ·
                      waiting {waitingDays(sub.createdAt)}
                    </p>
                    {sub.submittedVia === 'agent' && (
                      <span className='via-badge agent'>Agent</span>
                    )}
                  </div>
                  {sub.intelligentCheck && (
                    <div className='row'>
                      <IntelligentCheckBadge review={sub.intelligentCheck} />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {pagination && (
              <Pagination
                currentPage={pagination.page}
                totalCount={pagination.totalPages}
                pageSize={pagination.limit}
                onPageChange={(page: number, pageSize: number) =>
                  fetchQueue(page, pageSize)
                }
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default KycReview
